import React, {useEffect, useState} from 'react';
import * as yup from 'yup';
import {Controller, useForm} from 'react-hook-form';
import {yupResolver} from '@hookform/resolvers/yup';
import InputMUI from '../../../../shared/InputMUI';
import ButtonMUI from '../../../../shared/ButtonMUI';
import SelectComponent from '../../../../shared/SelectComponent';
import {useDispatch, useSelector} from 'react-redux';
import {AutocompleteMUI} from '../../../../shared';
import './ProjectsParticipantsAdd.scss';
import {useTranslation} from 'react-i18next';
import {getUserRoles} from '../../../../redux/actions/profile/profileActions';
import {
    getProject,
    getProjectMembersList,
    postProjectMembersCreate,
    getProjectEmployeesForAdding,
    getProjectClientsForAdding
} from '../../../../redux/actions/projects/projectsActions';
import CompaniesHero from '../../Companies/CompaniesHero';
import {mainPath} from '../../../../routes/paths';
import {useHistory} from 'react-router-dom';
import {passwordRegex} from "../../../../helpers/constants";
import {formatRolesByIerarchion} from "../../../../helpers/functions";

const ProjectsParticipantsAdd = () => {
    const history = useHistory();
    const {singleProject} = useSelector((state) => state.projects);
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const [buttonLoad, setButtonLoad] = useState(false);
    const {userRoles,profileMe} = useSelector((state) => state.profile);
    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
    const [selectedClientIds, setSelectedClientIds] = useState([]);

    const [firstName, setFirstName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [selectedPosition, setSelectedPosition] = useState(null);

    const isAnyFieldFilled = firstName || email || password || selectedPosition;

    const {projectEmployeesForAdding, projectClientsForAdding} = useSelector((state) => state.projects);
    const {projectMembersList, projectMembersListLoading} = useSelector((state) => state.projects);

    useEffect(() => {
        if (Object.keys(singleProject).length) {
            dispatch(getUserRoles(''));
            dispatch(getProjectEmployeesForAdding(singleProject.companyId, singleProject.id));
            dispatch(getProjectClientsForAdding(singleProject.companyId, singleProject.id));
        }
    }, [dispatch, singleProject]);

    const formattedRoles = formatRolesByIerarchion(profileMe?.role, userRoles);

    const position = formattedRoles?.map((role) => ({
        id: role.id,
        role: role.role,
        name: role.name
    }));

    const schema = yup.object().shape({
        employee: yup.array(),
        clients: yup.array(),
        firstName: yup.string().when(['employee', 'clients'], {
            is: (employees, clients) => employees.length === 0 && clients.length === 0,
            then: (schema) => schema.required(t('validations.required')),
            otherwise: (schema) => schema.notRequired(),
        }),
        position: yup.string().when(['employee', 'clients'], {
            is: (employees, clients) => employees.length === 0 && clients.length === 0,
            then: (schema) => schema.required(t('validations.required')),
            otherwise: (schema) => schema.notRequired(),
        }),
        phone: yup.string().when(['employee', 'clients'], {
            is: (employees, clients) => employees.length === 0 && clients.length === 0,
            then: (schema) => schema.required(t('validations.required')),
            otherwise: (schema) => schema.notRequired(),
        }),
        email: yup.string().when(['employee', 'clients'], {
            is: (employees, clients) => employees.length === 0 && clients.length === 0,
            then: (schema) => schema.required(t('validations.email')).email(t('validations.required')),
            otherwise: (schema) => schema.notRequired(),
        }),
        password: yup.string().when(['employee', 'clients'], {
            is: (employees, clients) => employees.length === 0 && clients.length === 0,
            then: (schema) => schema.matches(passwordRegex, t('validations.password'))
                .required('validations.required'),
            otherwise: (schema) => schema.notRequired(),
        }),
    });

    const {
        control,
        handleSubmit,
        reset,
        watch,
        setValue,
        clearErrors,
        formState: {errors}
    } = useForm({
        mode: 'all',
        reValidateMode: 'onChange',
        shouldFocusError: true,
        resolver: yupResolver(schema),
        defaultValues: {
            employee: [],
            clients: [],
            position: '',
            firstName: '',
            phone: '',
            email: '',
            password: ''
        }
    });

    const employeeArr = projectEmployeesForAdding.map((item) => {
        return {
            label: item.firstName ?? '',
            id: item.id,
            avatar: item.avatar ?? '',
            text: item.roleName
        };
    });

    const clientsArr = projectClientsForAdding.map((item) => {
        return {
            label: item.firstName ?? '',
            id: item.id,
            avatar: item.avatar ?? '',
            text: item.roleName,
            isSpectator: item.role === 'spectator',
        };
    });

    const watchedEmployees = watch('employee') || [];
    const watchedClients = watch('clients') || [];
    const watchedFirstName = watch('firstName') || '';
    const watchedPosition = watch('position') || '';
    const watchedPhone = watch('phone') || '';
    const watchedEmail = watch('email') || '';
    const watchedPassword = watch('password') || '';

    const isFormFilled = watchedEmployees.length > 0 || watchedClients.length > 0;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const isButtonDisabled =
        buttonLoad ||
        (!isFormFilled &&
            !(
                watchedFirstName.length >= 3 &&
                watchedPosition &&
                watchedPhone &&
                passwordRegex.test(watchedPassword) &&
                emailRegex.test(watchedEmail)
            ));

    const onSubmit = async (data) => {
        setButtonLoad(true);

        try {
            const formData = new FormData();
            if (selectedEmployeeIds.length > 0 || selectedClientIds.length > 0) {
                selectedEmployeeIds.forEach((employeeId, index) => {
                    formData.append(`employees[${index}]`, employeeId);
                });

                selectedClientIds.forEach((clientId, index) => {
                    formData.append(`clients[${index}]`, clientId);
                });
            } else {
                const selectedRole = position.find((role) => role.id === data.position);
                if (!selectedRole) {
                    throw new Error('Role not found');
                }
                formData.append('position', selectedRole.role);
                formData.append('firstName', data.firstName);
                formData.append('phone', data.phone);
                formData.append('email', data.email);
                formData.append('password', data.password);
            }

            const res = await dispatch(postProjectMembersCreate(formData, singleProject.id));
            if (res.payload?.status === 201) {
                setButtonLoad(false);
                dispatch(getProjectMembersList(singleProject.id));
                dispatch(getProject(singleProject.id,true));
                reset({
                    position: '',
                    firstName: '',
                    phone: '',
                    email: '',
                    password: ''
                });
                history.push(mainPath.projectsParticipants.replace(':id', singleProject?.id));
            } else {
                setButtonLoad(false);
            }
        } catch (err) {
            setButtonLoad(false);
        }
    };
    useEffect(() => {
        if (!projectMembersList.length && projectMembersListLoading) {
            dispatch(getProjectMembersList(singleProject.id));
        }
    }, [dispatch, singleProject.id, projectMembersListLoading, projectMembersList]);

    const handleBack = () => {
        if (history.length > 1) {
            history.goBack();
        } else {
            history.push(mainPath.projectsParticipants.replace(':id', singleProject?.id));
        }
    };


    const resetFieldsAdd = () => {
        clearErrors(['position', 'firstName', 'phone', 'email', 'password']);
    }

    return (
        <div className={'projects-participants-wrapper'}>
            <div className='projects-participants'>
                <div className='projects-name'>{singleProject.address}</div>

                <CompaniesHero
                    heading={`${projectMembersList?.clients?.length + projectMembersList?.employees?.length} ${t(
                        'ProjectsParticipants.heading'
                    )}`}
                    subHeading={t('ProjectsParticipants.subHeading')}
                    handleList={handleBack}
                    selectedBack={true}
                    selectedList={true}
                    selectedAdd={false}
                />
                <div className='participants-add'>
                    <div className='participants-add__form'>
                        <div className='participants-add__title'>{t('ProjectsParticipantsAdd.addParticipant')}</div>

                        <div className='participants-add__search'>
                            <div
                                className='participants-add__search-title'>{t('ProjectsParticipantsAdd.addEmployee')}</div>
                            <Controller
                                name='employee'
                                control={control}
                                render={({field}) => (
                                    <AutocompleteMUI
                                        className='no-icons'
                                        text={t('ProjectsParticipantsAdd.selectFromList')}
                                        array={employeeArr}
                                        value={selectedEmployeeIds.map((id) => employeeArr.find((emp) => emp.id === id))}
                                        error={errors?.employee}
                                        onChange={(values) => {
                                            const selectedValues = values.map((val) => val.id);
                                            setSelectedEmployeeIds(selectedValues);
                                            field.onChange(selectedValues);
                                            resetFieldsAdd()
                                        }}
                                        multiple={true}
                                        disabled={!!isAnyFieldFilled}
                                    />
                                )}
                            />
                        </div>

                        <hr/>

                        <div className='participants-add__search'>
                            <div
                                className='participants-add__search-title'>{t('ProjectsParticipantsAdd.addClient')}</div>
                            <Controller
                                name='clients'
                                control={control}
                                render={({field}) => (
                                    <AutocompleteMUI
                                        className='no-icons'
                                        text={t('ProjectsParticipantsAdd.selectFromList')}
                                        array={clientsArr}
                                        value={selectedClientIds.map((id) => clientsArr.find((client) => client.id === id))}
                                        error={errors?.clients}
                                        onChange={(values) => {
                                            const selectedValues = values.map((val) => val.id);
                                            setSelectedClientIds(selectedValues);
                                            field.onChange(selectedValues);
                                            resetFieldsAdd()
                                        }}
                                        multiple={true}
                                        disabled={!!isAnyFieldFilled}
                                    />
                                )}
                            />
                        </div>
                    </div>

                    <form className='participants-add__form' onSubmit={handleSubmit(onSubmit)}>
                        <div className='participants-add__info-text'>
                            <div className='text'>{t('ProjectsParticipantsAdd.orRegisterNewUser')}</div>
                        </div>

                        <Controller
                            name='position'
                            control={control}
                            render={({field}) => (
                                <SelectComponent
                                    onChange={(e) => {
                                        const val = e ? e?.target?.value : null;
                                        field.onChange(e ?? null);
                                        setSelectedPosition(val);
                                    }}
                                    onBlur={field.onBlur}
                                    value={position.find((c) => c.id === field.value)}
                                    options={position}
                                    error={errors.position?.message}
                                    label={t('ProjectsParticipantsAdd.role')}
                                    placeholder=''
                                    inputProps={field}
                                    disabled={selectedEmployeeIds.length > 0 || selectedClientIds.length > 0}
                                />
                            )}
                        />

                        <div className='form__input'>
                            <Controller
                                name='firstName'
                                control={control}
                                render={({field}) => (
                                    <InputMUI
                                        type='text'
                                        fullWidth
                                        className='no-icons'
                                        disabled={selectedEmployeeIds.length > 0 || selectedClientIds.length > 0}
                                        error={errors.firstName?.message}
                                        inputProps={field}
                                        label={t('ProjectsParticipantsAdd.firstName')}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setFirstName(e.target.value);
                                        }}
                                    />
                                )}
                            />
                        </div>

                        <div className='form__input'>
                            <Controller
                                name='phone'
                                control={control}
                                render={({field, fieldState: {error}}) => (
                                    <InputMUI
                                        className='no-icons'
                                        type='tel'
                                        fullWidth
                                        error={error?.message}
                                        label={t('ProjectsParticipantsAdd.phone')}
                                        disabled={selectedEmployeeIds.length > 0 || selectedClientIds.length > 0}
                                        onChange={(e) => {
                                            const value = e.target.value;

                                            let cleanValue = value.replace(/[^0-9+]/g, ''); // Remove anything that's not a digit or '+'

                                            if (cleanValue.charAt(0) !== '+') {
                                                cleanValue = cleanValue.replace(/\+/g, ''); // Remove any '+' that is not at the start
                                            } else {
                                                cleanValue = '+' + cleanValue.replace(/\+/g, ''); // Keep only the first '+' and remove others
                                            }

                                            if (/^\+?\d*$/.test(cleanValue)) {
                                                field.onChange(cleanValue);
                                            }
                                        }}
                                        inputProps={field}
                                    />
                                )}
                            />
                        </div>

                        <div className='form__input'>
                            <Controller
                                name='email'
                                control={control}
                                render={({field}) => (
                                    <InputMUI
                                        type='email'
                                        fullWidth
                                        className='no-icons'
                                        disabled={selectedEmployeeIds.length > 0 || selectedClientIds.length > 0}
                                        error={errors.email?.message}
                                        inputProps={field}
                                        label={t('ProjectsParticipantsAdd.email')}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setEmail(e.target.value);
                                        }}
                                    />
                                )}
                            />
                        </div>

                        <div className='form__input'>
                            <Controller
                                name='password'
                                control={control}
                                render={({field}) => (
                                    <InputMUI
                                        type='password'
                                        fullWidth
                                        className='no-icons'
                                        disabled={selectedEmployeeIds.length > 0 || selectedClientIds.length > 0}
                                        error={errors.password?.message}
                                        inputProps={
                                            {
                                                ...field,
                                                role: 'presentation',
                                                autoComplete: 'new-password',
                                            }
                                        }
                                        label={t('ProjectsParticipantsAdd.password')}
                                        onChange={(e) => {
                                            field.onChange(e);
                                            setPassword(e.target.value);
                                        }}
                                    />
                                )}
                            />
                        </div>

                        <ButtonMUI disabled={isButtonDisabled} loading={buttonLoad} formAction>
                            {t('ProjectsParticipantsAdd.add')}
                        </ButtonMUI>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ProjectsParticipantsAdd;
